import { useState, useEffect, useCallback, useRef } from "react";
import { SESSION_STORAGE } from "../constants/sessionStorage";
import keycloak from "../Keycloak";

const WARNING_BUFFER_MS = 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 5 * 1000;

export const useKeycloak = () => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState("");
  const [tokenParsed, setTokenParsed] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const sessionTimerRef = useRef(null);
  const refreshToken = useRef(null);

  const setupSessionTimer = useCallback(() => {
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
    }

    sessionTimerRef.current = setInterval(() => {
      if (!refreshToken.current || !refreshToken.current.exp) return;

      const skewMs = (keycloak.timeSkew || 0) * 1000;

      const expiryTimeMs = refreshToken.current.exp * 1000 + skewMs;
      const now = Date.now();
      const timeUntilExpiry = expiryTimeMs - now;

      if (timeUntilExpiry > 0 && timeUntilExpiry <= WARNING_BUFFER_MS) {
        setShowSessionExpiredModal(true);
      } else if (timeUntilExpiry <= 0) {
        clearInterval(sessionTimerRef.current);
        setShowSessionExpiredModal(false);
        keycloak.logout();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, []);

  useEffect(() => {
    if (authenticated && tokenParsed) {
      setupSessionTimer();
    }
  }, [authenticated, tokenParsed, setupSessionTimer]);

  useEffect(() => {
    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (initialized || keycloak.initialized) {
      return;
    }

    keycloak
      .init({
        pkceMethod: "S256",
        onLoad: "login-required",
        scope: "hospi_data",
        checkLoginIframe: false,
        redirectUri: `${window.location.origin}/hospiadmission/en/search`,
      })
      .then((auth) => {
        setInitialized(true);
        keycloak.initialized = true;
        setAuthenticated(auth);
        if (auth) {
          if (keycloak.token) {
            setToken(keycloak.token);
            sessionStorage.setItem(
              SESSION_STORAGE.KEYCLOAK_TOKEN,
              keycloak.token,
            );
          }

          if (keycloak.tokenParsed) {
            setTokenParsed(keycloak.tokenParsed);
          }

          if (keycloak.refreshTokenParsed) {
            refreshToken.current = keycloak.refreshTokenParsed;
          }
          keycloak.loadUserInfo().then((info) => setUserInfo(info));
        }
      })
      .catch((error) => {
        console.error("Keycloak initialization failed:", error);
        setInitialized(true);
      });
  }, [initialized]);

  const login = useCallback(() => {
    keycloak.login();
  }, []);

  const logout = useCallback(() => {
    keycloak.logout();
  }, []);

  const register = useCallback(() => {
    keycloak.register();
  }, []);

  const accountManagement = useCallback(() => {
    keycloak.accountManagement();
  }, []);

  const isTokenExpired = useCallback((minValidity) => {
    return keycloak.isTokenExpired(minValidity);
  }, []);

  const updateToken = useCallback((minValidity) => {
    keycloak
      .updateToken(minValidity, true)
      .then((refreshed) => {
        if (refreshed) {
          if (keycloak.token) {
            setToken(keycloak.token);
            sessionStorage.setItem(
              SESSION_STORAGE.KEYCLOAK_TOKEN,
              keycloak.token,
            );
          }
          if (keycloak.tokenParsed) setTokenParsed(keycloak.tokenParsed);
          if (keycloak.refreshTokenParsed)
            refreshToken.current = keycloak.refreshTokenParsed;
        }
      })
      .catch(() => {
        console.warn("Failed to refresh token manually");
      });
  }, []);

  return {
    initialized,
    authenticated,
    token,
    tokenParsed,
    userInfo,
    showSessionExpiredModal,
    setShowSessionExpiredModal,
    login,
    logout,
    register,
    accountManagement,
    updateToken,
    isTokenExpired,
  };
};
